import { addDays, diffDays } from './dates.js';
export const DOC_STATUSES = ['Planejado', 'Em elaboração', 'Em verificação', 'Aprovação do gerente', 'Aprovação do diretor', 'Em aprovação', 'Comentado', 'Em revisão', 'Aprovado', 'Liberado para construção', 'As Built', 'Rejeitado', 'Cancelado', 'Substituído'];
export const TERMINAL = new Set(['Cancelado', 'Substituído']);
/** Pesos combinados com a VMM (21/10/2026): elaboração 10, gerente 30, diretor 40, emitido ao cliente 50, revisão 70, aprovado 100. */
export const DEFAULT_STATUS_WEIGHTS = {
    'Planejado': 0, 'Em elaboração': 10, 'Em verificação': 30, 'Aprovação do gerente': 30, 'Aprovação do diretor': 40, 'Em aprovação': 50, 'Comentado': 50, 'Em revisão': 70,
    'Rejeitado': 30, 'Aprovado': 100, 'Liberado para construção': 100, 'As Built': 100
};
export function isAccepted(d, t) { return t.accept.includes(d.status); }
/** % do documento pelos marcos ponderados do tipo. 100% somente no critério de aceite; emitir não conclui. */
export function docPercent(d, t) {
    if (TERMINAL.has(d.status))
        return 0;
    if (isAccepted(d, t))
        return 100;
    const w = { ...DEFAULT_STATUS_WEIGHTS, ...t.weights }[d.status] ?? 0;
    return Math.min(99, Math.max(0, w));
}
/** % planejado na data: rampa até a emissão (peso de "Em aprovação") e depois até o aceite (100%). */
export function docPlannedPercent(d, t, date) {
    if (TERMINAL.has(d.status) || !d.planIssue)
        return 0;
    const weights = { ...DEFAULT_STATUS_WEIGHTS, ...t.weights };
    const s0 = d.planStart ?? addDays(d.planIssue, -30);
    const acceptDate = t.accept.includes('Aprovado') ? (d.planApproval ?? addDays(d.planIssue, 20)) : (d.planIfc ?? d.planApproval ?? addDays(d.planIssue, 30));
    const pts = [[s0, 0], [d.planIssue, Math.min(99, weights['Em aprovação'] ?? 50)], [acceptDate, 100]];
    if (date <= pts[0][0])
        return 0;
    for (let i = 1; i < pts.length; i++) {
        const [d1, v1] = pts[i], [d0, v0] = pts[i - 1];
        if (date <= d1)
            return v0 + (v1 - v0) * diffDays(d0, date) / Math.max(1, diffDays(d0, d1));
    }
    return 100;
}
/** Próxima data planejada ainda não cumprida (para atraso e alertas). */
export function docDueDate(d, t) {
    if (TERMINAL.has(d.status) || isAccepted(d, t))
        return null;
    const issued = d.issued || !INTERNAL.has(d.status);
    if (!issued)
        return d.planIssue ?? null;
    return t.accept.includes('Aprovado') ? (d.planApproval ?? null) : (d.planIfc ?? d.planApproval ?? null);
}
export const isDocLate = (d, t, today) => { const x = docDueDate(d, t); return !!x && x < today; };
export const DOC_ACTOR_LABEL = { chefe: 'Chefe da disciplina', gerente: 'Gerente do projeto', diretor: 'Diretor de engenharia', cliente: 'Cliente' };
/** Situações antes da emissão ao cliente. */
const INTERNAL = new Set(['Planejado', 'Em elaboração', 'Em verificação', 'Aprovação do gerente', 'Aprovação do diretor']);
const ALWAYS = new Set(['Em elaboração', 'Aprovação do gerente', 'Aprovação do diretor', 'Em aprovação', 'Em revisão', 'Cancelado', 'Substituído']);
export function transitions(d, t) {
    const has = (s) => ALWAYS.has(s) || t.statuses.includes(s);
    const T = [];
    const add = (x) => { if (has(x.to))
        T.push({ ...x, need: x.by === 'chefe' ? 'edit' : 'approve' }); };
    const back = d.issued ? 'Em revisão' : 'Em elaboração';
    switch (d.status) {
        case 'Planejado':
            add({ to: 'Em elaboração', label: 'Iniciar elaboração', by: 'chefe' });
            break;
        case 'Em elaboração':
        case 'Em verificação':
        case 'Em revisão':
            add({ to: 'Aprovação do gerente', label: 'Encaminhar ao gerente', by: 'chefe', needsFile: true });
            break;
        case 'Aprovação do gerente':
            add({ to: 'Aprovação do diretor', label: 'Aprovar e enviar ao diretor', by: 'gerente' });
            add({ to: back, label: 'Devolver ao chefe da disciplina', by: 'gerente', requiresReason: true });
            break;
        case 'Aprovação do diretor':
            add({ to: 'Em aprovação', label: 'Aprovar e emitir ao cliente', by: 'diretor', issue: true, needsFile: true });
            add({ to: back, label: 'Devolver ao chefe da disciplina', by: 'diretor', requiresReason: true });
            break;
        case 'Em aprovação':
            add({ to: 'Aprovado', label: 'Aprovar', by: 'cliente', approve: true });
            add({ to: 'Comentado', label: 'Devolver com comentários', by: 'cliente', requiresReason: true });
            add({ to: 'Rejeitado', label: 'Rejeitar', by: 'cliente', requiresReason: true });
            break;
        case 'Comentado':
            add({ to: 'Em revisão', label: 'Iniciar revisão', by: 'chefe', bump: true });
            break;
        case 'Rejeitado':
            add({ to: 'Em revisão', label: 'Reemitir em nova revisão', by: 'chefe', bump: true });
            break;
        case 'Aprovado':
            if (has('Liberado para construção'))
                add({ to: 'Liberado para construção', label: 'Liberar para construção', by: 'gerente', ifc: true });
            break;
        case 'Liberado para construção':
            if (t.asBuilt)
                add({ to: 'As Built', label: 'Registrar As Built', by: 'chefe', bump: true });
            add({ to: 'Em revisão', label: 'Abrir nova revisão', by: 'chefe', bump: true });
            break;
        case 'As Built':
            add({ to: 'Em revisão', label: 'Abrir nova revisão', by: 'chefe', bump: true });
            break;
    }
    if (!TERMINAL.has(d.status)) {
        add({ to: 'Cancelado', label: 'Cancelar documento', by: 'gerente', requiresReason: true });
        add({ to: 'Substituído', label: 'Marcar como substituído', by: 'gerente', requiresReason: true, supersede: true });
    }
    return T;
}
/** Etapas do fluxo, na ordem em que aparecem na imagem do documento. */
export const DOC_STAGES = [
    { key: 'elaboracao', label: 'Elaboração', actor: 'chefe' },
    { key: 'gerente', label: 'Gerente do projeto', actor: 'gerente' },
    { key: 'diretor', label: 'Diretor de engenharia', actor: 'diretor' },
    { key: 'cliente', label: 'Cliente', actor: 'cliente' },
    { key: 'aprovado', label: 'Aprovado', actor: null }
];
/** Índice da etapa atual em DOC_STAGES (4 = aprovado), ou null para cancelado e substituído. */
export function docStage(status) {
    switch (status) {
        case 'Planejado':
        case 'Em elaboração':
        case 'Em verificação':
        case 'Em revisão':
        case 'Comentado':
        case 'Rejeitado': return 0;
        case 'Aprovação do gerente': return 1;
        case 'Aprovação do diretor': return 2;
        case 'Em aprovação': return 3;
        case 'Aprovado':
        case 'Liberado para construção':
        case 'As Built': return 4;
        default: return null;
    }
}
/** Quem precisa agir agora (e recebe o aviso). Planejado ainda não pede ação. */
export function docActor(status) {
    if (status === 'Planejado')
        return null;
    const i = docStage(status);
    return i === null || i === 4 ? null : DOC_STAGES[i].actor;
}
export function nextLetter(s) {
    const u = s.toUpperCase();
    if (!u)
        return 'A';
    const last = u.charCodeAt(u.length - 1);
    return last < 90 ? u.slice(0, -1) + String.fromCharCode(last + 1) : nextLetter(u.slice(0, -1) || '@') + 'A';
}
/** Próxima revisão: A, B, C... até a liberação; 0, 1, 2 depois dela (padrão letras-numeros). */
export function nextRevision(current, scheme, toIfc = false) {
    const cur = current ?? '';
    const numeric = /^\d+$/.test(cur);
    if (scheme === 'numeros')
        return cur === '' ? '0' : String((Number(cur) || 0) + 1);
    if (scheme === 'letras')
        return cur ? nextLetter(cur) : 'A';
    if (toIfc)
        return '0';
    if (numeric)
        return String(Number(cur) + 1);
    return cur ? nextLetter(cur) : 'A';
}
/** Valida a configuração de um tipo documental. */
export function validateDocType(t) {
    const e = [];
    if (!t.accept.length)
        e.push('Defina ao menos um critério de aceite.');
    for (const a of t.accept)
        if (!t.statuses.includes(a))
            e.push(`O critério de aceite "${a}" precisa estar no fluxo.`);
    for (const [s, w] of Object.entries(t.weights))
        if (w === undefined || w < 0 || w > 100)
            e.push(`Peso inválido em "${s}".`);
    return e;
}
//# sourceMappingURL=docflow.js.map